import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import type { Todo, TodoCreate } from '../types';
import { TaskStatus } from '../types';
import { extractDatePart } from '../utils/dateUtils';
import {
  initializeFormData,
  updateFormDataFromTodo,
  prepareSubmitData,
  validateFormData,
  parseTagsFromInput,
  formatTagsToInput,
  type FormData
} from '../utils/formUtils';
import { useTaskFieldSettings } from '../hooks/useTaskFieldSettings';
import TodoFormDocument from './TodoFormDocument';
import { TaskAIPanel } from './TaskAIPanel';
import { useAuth } from '../contexts/AuthContext';
import TodoComments from './TodoComments';
import { useEventsForTodo } from '../hooks/useEvents';
import TodoFormHeader from './TodoFormHeader';
import TodoFormStatusRow from './TodoFormStatusRow';
import TodoFormScheduling from './TodoFormScheduling';
import TodoFormPrimaryFields from './TodoFormPrimaryFields';
import TodoFormSecondaryFields from './TodoFormSecondaryFields';
import TodoFormLinkedEvents from './TodoFormLinkedEvents';
import TodoFormParentTask from './TodoFormParentTask';
import TodoFormSubtasks from './TodoFormSubtasks';
import TodoFormTeamFields from './TodoFormTeamFields';

interface TodoFormProps {
  todo?: Todo;
  availableTags?: string[];
  allTodos?: Todo[]; // For subtask parent selection
  initialParentId?: string; // New prop for pre-filling parent_id
  initialDeadline?: string; // New prop for pre-filling deadline
  initialTeamId?: string; // New prop for pre-filling team_id
  currentUser?: { id: string; name: string; email: string; }; // Current user info
  teamMembers?: { id: string; name: string; email: string; }[]; // Team members for assignee
  isPersonalMode?: boolean; // Whether we're in personal team mode
  onSubmit: (data: TodoCreate) => void;
  onCancel: () => void;
  onDelete?: (todoId: string) => void;
  onClone?: (todoId: string) => void; // New prop for cloning
  onSubtaskClick?: (todo: Todo) => void; // New prop for clicking subtasks
  onParentClick?: (todo: Todo) => void; // New prop for clicking parent
  onStartPomodoro?: (todo: Todo) => void; // New prop for starting pomodoro timer
  onAutoSave?: (data: TodoCreate) => void; // New prop for auto-saving
  isLoading?: boolean;
}

export interface TodoFormRef {
  autoSave: () => void;
  hasChanges: () => boolean;
  hasTitle: () => boolean;
}

const TodoForm = forwardRef<TodoFormRef, TodoFormProps>(({
  todo,
  availableTags = [],
  allTodos = [],
  initialParentId,
  initialDeadline,
  initialTeamId,
  currentUser,
  teamMembers = [],
  isPersonalMode = true,
  onSubmit,
  onCancel,
  onDelete,
  onClone,
  onSubtaskClick,
  onParentClick,
  onAutoSave,
  onStartPomodoro,
  isLoading = false
}, ref) => {
  const { user } = useAuth();
  const fieldSettings = useTaskFieldSettings();
  const { events: linkedEvents } = useEventsForTodo(todo?.id);
  const [formData, setFormData] = useState<FormData>(() =>
    initializeFormData(todo, initialDeadline, undefined, initialParentId, initialTeamId)
  );

  const [tagInput, setTagInput] = useState(() =>
    todo ? formatTagsToInput(todo.tags) : ''
  );
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showMoreFields, setShowMoreFields] = useState(false);
  const tagInputRef = useRef<HTMLInputElement>(null);

  // Tag suggestion filtering
  const getFilteredTagSuggestions = () => {
    if (!tagInput) return availableTags.filter(tag => !formData.tags.includes(tag));

    const currentTags = tagInput.split(',').map(tag => tag.trim());
    const lastTag = currentTags[currentTags.length - 1].toLowerCase();

    return availableTags.filter(tag =>
      tag.toLowerCase().includes(lastTag) &&
      !formData.tags.includes(tag)
    );
  };

  const handleTagSuggestionClick = (suggestedTag: string) => {
    const currentTags = tagInput.split(',').map(tag => tag.trim());
    currentTags[currentTags.length - 1] = suggestedTag;
    setTagInput(currentTags.join(', ') + ', ');
    setShowTagSuggestions(false);
    tagInputRef.current?.focus();
  };

  // Get children of the current task
  const children = todo ? allTodos.filter(t => t.parent_id === todo.id) : [];
  const hasChildren = children.length > 0;

  // Get parent task if this is a child task
  const parentTask = todo && todo.parent_id ? allTodos.find(t => t.id === todo.parent_id) ?? null : null;

  // Update form when todo changes (for editing)
  useEffect(() => {
    if (todo) {
      setFormData(updateFormDataFromTodo(todo));
      setTagInput(formatTagsToInput(todo.tags));
    }
  }, [todo]);

  // Update tags when tagInput changes
  useEffect(() => {
    const tags = parseTagsFromInput(tagInput);
    setFormData(prev => ({ ...prev, tags }));
  }, [tagInput]);

  const validateForm = () => {
    const newErrors = validateFormData(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling on mobile

    if (!validateForm()) {
      // Focus on the first error field
      if (errors.title) {
        const titleInput = document.querySelector('input[type="text"]') as HTMLInputElement;
        titleInput?.focus();
      }
      return;
    }

    // Additional validation for empty title (trim whitespace)
    if (!formData.title.trim()) {
      setErrors({ title: 'Title cannot be empty' });
      const titleInput = document.querySelector('input[type="text"]') as HTMLInputElement;
      titleInput?.focus();
      return;
    }

    // Set creator_id for new tasks
    const finalFormData = { ...formData };
    if (!todo && currentUser && !finalFormData.creator_id) {
      finalFormData.creator_id = currentUser.id;
    }

    const submitData = prepareSubmitData(finalFormData);
    onSubmit(submitData);
  };

  const autoSave = () => {
    // Only auto-save if there are changes and the title is not empty
    if (!formData.title.trim()) {
      return;
    }

    // Set creator_id for new tasks
    const finalFormData = { ...formData };
    if (!todo && currentUser && !finalFormData.creator_id) {
      finalFormData.creator_id = currentUser.id;
    }

    const submitData = prepareSubmitData(finalFormData);

    if (onAutoSave) {
      onAutoSave(submitData);
    } else {
      onSubmit(submitData);
    }
  };

  const hasChanges = () => {
    if (!todo) return false; // No changes if creating new todo

    // Compare current form data with original todo data
    const originalData = {
      title: todo.title,
      description: todo.description || '',
      status: todo.status,
      horizon: todo.horizon || '',
      deadline: todo.deadline ? extractDatePart(todo.deadline) : '',
      recurrence_interval: todo.recurrence_interval,
      tags: todo.tags,
      notes: todo.notes || '',
      next_step: todo.next_step || '',
      parent_id: todo.parent_id || '',
      team_id: todo.team_id || '',
      creator_id: todo.creator_id || '',
      assigned_to_id: todo.assigned_to_id || '',
      estimated_time_minutes: todo.estimated_time_minutes || '',
    };

    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  // Helper function to check if a todo is a descendant of another todo
  const isDescendantOf = (potentialChild: Todo, potentialParent: Todo, visited = new Set<string>()): boolean => {
    // Prevent infinite recursion
    if (visited.has(potentialParent.id)) {
      return false;
    }

    if (potentialChild.id === potentialParent.id) return true;

    // Add current parent to visited set
    visited.add(potentialParent.id);

    // Find all children of potentialParent
    const childrenOfParent = allTodos.filter(t => t.parent_id === potentialParent.id);

    // Check if potentialChild is among the children or their descendants
    const result = childrenOfParent.some(child =>
      child.id === potentialChild.id || isDescendantOf(potentialChild, child, visited)
    );

    // Remove from visited set when backtracking
    visited.delete(potentialParent.id);

    return result;
  };

  // Get valid parent options (exclude self and descendants)
  const getValidParentOptions = () => {
    if (!todo) return allTodos; // For new todos, all todos are valid parents

    return allTodos.filter(t => {
      // Exclude self
      if (t.id === todo.id) return false;

      // Exclude current descendants to prevent circular relationships
      return !isDescendantOf(t, todo);
    });
  };

  // Sort parent options: active statuses first, then by child count (desc), then by last modified (desc)
  const validParentOptions = getValidParentOptions().sort((a, b) => {
    // Completed tasks go to bottom
    const aCompleted = a.status === TaskStatus.COMPLETED ? 1 : 0;
    const bCompleted = b.status === TaskStatus.COMPLETED ? 1 : 0;
    if (aCompleted !== bCompleted) return aCompleted - bCompleted;

    // More children = higher (better parent candidates)
    const aChildren = allTodos.filter(t => t.parent_id === a.id).length;
    const bChildren = allTodos.filter(t => t.parent_id === b.id).length;
    if (aChildren !== bChildren) return bChildren - aChildren;

    // Most recently modified first
    return new Date(b.last_modified_time).getTime() - new Date(a.last_modified_time).getTime();
  });

  // Get selected parent display name
  const selectedParentName = formData.parent_id
    ? validParentOptions.find(p => p.id === formData.parent_id)?.title || 'Unknown Parent'
    : 'No Parent Task';

  useImperativeHandle(ref, () => ({
    autoSave,
    hasChanges,
    hasTitle: () => !!formData.title.trim()
  }));

  return (
    <div className="relative">
      <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="space-y-4 touch-manipulation">
      {/* Header with title and action buttons */}
      <TodoFormHeader
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        todo={todo}
        isLoading={isLoading}
        onCancel={onCancel}
        onClone={onClone}
        onDelete={onDelete}
      />

      {/* Status Row */}
      <TodoFormStatusRow
        formData={formData}
        setFormData={setFormData}
        todo={todo}
        onStartPomodoro={onStartPomodoro}
      />

      {/* Scheduling Buttons */}
      <TodoFormScheduling
        formData={formData}
        setFormData={setFormData}
      />

      {/* Primary Fields Row: Deadline | Tags */}
      <TodoFormPrimaryFields
        formData={formData}
        setFormData={setFormData}
        fieldSettings={fieldSettings}
        tagInput={tagInput}
        setTagInput={setTagInput}
        tagInputRef={tagInputRef}
        availableTags={availableTags}
        showTagSuggestions={showTagSuggestions}
        setShowTagSuggestions={setShowTagSuggestions}
        getFilteredTagSuggestions={getFilteredTagSuggestions}
        handleTagSuggestionClick={handleTagSuggestionClick}
      />

      {/* Description */}
      {fieldSettings.description && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className={`input-field resize-y ${errors.description ? 'border-red-500 focus:ring-red-500' : ''}`}
            rows={4}
            placeholder="Add more details..."
          />
          {errors.description && (
            <p className="text-red-500 text-xs mt-1">{errors.description}</p>
          )}
        </div>
      )}

      {/* Progressive Disclosure: More Fields */}
      <TodoFormSecondaryFields
        formData={formData}
        setFormData={setFormData}
        fieldSettings={fieldSettings}
        showMoreFields={showMoreFields}
        setShowMoreFields={setShowMoreFields}
      />

      {/* Linked Events */}
      {todo && (
        <TodoFormLinkedEvents events={linkedEvents} />
      )}

      {/* Document Section - only for existing tasks */}
      {todo && todo.id && (
        <TodoFormDocument todoId={todo.id} todoTitle={todo.title} />
      )}

      {/* Parent Task (Subtask Support) */}
      {fieldSettings.parentTask && (
        <TodoFormParentTask
          formData={formData}
          setFormData={setFormData}
          todo={todo}
          parentTask={parentTask}
          filteredParentOptions={validParentOptions}
          selectedParentName={selectedParentName}
          onParentClick={onParentClick}
        />
      )}

      {/* Children/Subtasks Section (only show when editing and has children) */}
      {todo && hasChildren && (
        <TodoFormSubtasks
          children={children}
          onSubtaskClick={onSubtaskClick}
        />
      )}

      {/* Team-specific Fields */}
      {!isPersonalMode && (
        <TodoFormTeamFields
          formData={formData}
          setFormData={setFormData}
          fieldSettings={fieldSettings}
          todo={todo}
          teamMembers={teamMembers}
        />
      )}


      </form>

      {/* AI Assistant Panel - only show for existing tasks and when AI features enabled */}
      {todo && todo.id && user?.preferences?.aiFeaturesEnabled === true && (
        <TaskAIPanel
          taskId={todo.id}
          taskTitle={todo.title}
        />
      )}

      {/* Comments Section - only show for existing tasks */}
      {todo && todo.id && (
        <TodoComments
          todoId={todo.id}
          currentUser={currentUser}
          isPersonalMode={isPersonalMode}
          createdTime={todo.created_time}
        />
      )}
    </div>
  );
});

export default TodoForm;
